import { useMemo, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PERIOD_PRESETS, resolvePeriod, comparisonPeriod, snapshotAt, fmtDate } from "./trendMath";
import { money } from "./rateMath";
import { Section } from "./Section";

const METRICS = [
  { key: "true_hourly_cost", label: "True Hourly Cost", fmt: (v) => `${money(v)}/hr`, pct: true },
  { key: "utilization_pct", label: "Utilization", fmt: (v) => `${v}%`, points: true },
  { key: "total_overhead_yearly", label: "Total Overhead", fmt: (v) => `${money(v, 0)}/yr`, pct: true },
  { key: "suggested_rate", label: "Suggested Labour Rate", fmt: (v) => `${money(v)}/hr`, pct: true },
  { key: "num_staff", label: "Number of Staff", fmt: (v) => `${v}` },
  { key: "average_wage_yearly", label: "Average Wage (blended)", fmt: (v) => `${money(v, 0)}/yr`, pct: true },
];

const deltaText = (m, a, b) => {
  const d = a - b;
  if (m.points) return `${d >= 0 ? "+" : ""}${Math.round(d * 10) / 10} pts`;
  if (m.pct && b) return `${d >= 0 ? "+" : ""}${Math.round((d / b) * 1000) / 10}%`;
  return `${d >= 0 ? "+" : ""}${Math.round(d * 100) / 100}`;
};

const PeriodValue = ({ snap, m, testId }) =>
  snap ? (
    <span data-testid={testId}>
      <span className="font-mono font-semibold text-gray-900">{m.fmt(snap[m.key])}</span>
      <span className="block text-[10px] text-gray-400">saved {fmtDate(snap.saved_at)}</span>
    </span>
  ) : (
    <span className="text-gray-400" data-testid={testId}>no data</span>
  );

const Comparison = ({ snapshots, period, compare }) => {
  const a = period ? snapshotAt(snapshots, period.end) : null;
  const b = compare ? snapshotAt(snapshots, compare.end) : null;
  return (
    <table className="w-full text-xs mt-3" data-testid="trend-comparison">
      <thead>
        <tr className="text-left text-gray-500">
          <th className="pb-1 font-medium">Metric</th>
          <th className="pb-1 font-medium">This period</th>
          <th className="pb-1 font-medium">Compared period</th>
          <th className="pb-1 font-medium text-right">Change</th>
        </tr>
      </thead>
      <tbody>
        {METRICS.map((m) => (
          <tr key={m.key} className="border-t border-gray-100 align-top">
            <td className="py-1.5 text-gray-700">{m.label}</td>
            <td className="py-1.5"><PeriodValue snap={a} m={m} testId={`trend-a-${m.key}`} /></td>
            <td className="py-1.5"><PeriodValue snap={b} m={m} testId={`trend-b-${m.key}`} /></td>
            <td className="py-1.5 text-right font-mono" data-testid={`trend-delta-${m.key}`}>{a && b ? deltaText(m, a[m.key], b[m.key]) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const TrendChart = ({ snapshots }) => {
  const data = snapshots.map((s) => ({
    date: new Date(s.saved_at).toLocaleString("en-AU", { day: "numeric", month: "short", year: "2-digit", hour: "numeric", minute: "2-digit" }),
    cost: s.true_hourly_cost,
    util: s.utilization_pct,
  }));
  if (data.length === 0) return <div className="text-xs text-gray-400 py-6 text-center">Save the calculator to start tracking trends.</div>;
  return (
    <div className="h-56 mt-4" data-testid="trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
          <YAxis yAxisId="cost" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
          <YAxis yAxisId="util" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
          <Tooltip contentStyle={{ fontSize: 11 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line yAxisId="cost" type="monotone" dataKey="cost" name="True Hourly Cost ($/hr)" stroke="#db2777" strokeWidth={2} dot={{ r: 3 }} />
          <Line yAxisId="util" type="monotone" dataKey="util" name="Utilization (%)" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const TrendTracking = ({ snapshots }) => {
  const [preset, setPreset] = useState("last12");
  const [mode, setMode] = useState("previous");
  const [custom, setCustom] = useState({ from: "", to: "" });
  // "now" is never earlier than the latest save, so a fresh save always lands in the current period
  const period = useMemo(() => {
    const last = snapshots.length ? new Date(snapshots[snapshots.length - 1].saved_at) : null;
    const now = last && last > new Date() ? last : new Date();
    return resolvePeriod(preset, custom, now);
  }, [preset, custom, snapshots]);
  const compare = useMemo(() => comparisonPeriod(period, mode), [period, mode]);
  return (
    <Section letter="E" title="Trend tracking" description="Each save is stored as a snapshot. Each period uses the most recent save on or before its end date." testId="rate-calc-trends">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="trend-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIOD_PRESETS.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`trend-period-${p.id}`}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {preset === "custom" && (
          <>
            <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="h-8 w-[150px] text-xs" data-testid="trend-custom-from" />
            <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="h-8 w-[150px] text-xs" data-testid="trend-custom-to" />
          </>
        )}
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="trend-compare-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="previous" data-testid="trend-compare-previous">Compare to: Previous period</SelectItem>
            <SelectItem value="lastYear" data-testid="trend-compare-last-year">Compare to: Same period last year</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {period && compare && (
        <div className="text-[11px] text-gray-500 mt-2" data-testid="trend-period-range">
          {fmtDate(period.start)} – {fmtDate(period.end)} vs {fmtDate(compare.start)} – {fmtDate(compare.end)}
        </div>
      )}
      {period ? <Comparison snapshots={snapshots} period={period} compare={compare} /> : <div className="text-xs text-gray-400 mt-3">Pick a start and end date.</div>}
      <TrendChart snapshots={snapshots} />
    </Section>
  );
};
